import { getBranchesById } from '@/apis/branches';
import { getCampaignsByIds } from '@/apis/campaigns';
import { getCatCampaignsByIds } from '@/apis/catcampaigns';
import { getElectionById, getElectionsByIds } from '@/apis/elections';
import { getEventsByIds } from '@/apis/events';
import { getEventTypes } from '@/apis/eventTypes';
import { getGeometries } from '@/apis/geometries';
import { getGroups } from '@/apis/groups';
import { getListsByIds } from '@/apis/lists';
import { getMailingsByIds } from '@/apis/mailings';
import { getTagsByIds } from '@/apis/tags';
import { getUsersByIds } from '@/apis/users';
import { DomainCode } from '@australiangreens/ag-internal-components';
import { isTemplatePlaceholder } from '@/types';
import { mapCampaignsToTooltips } from '@/constants/filterCriteria/callingStatusOptions';
import { PRETTY_GEOM_TYPES } from '@/constants/filterCriteria/location';

import {
  findPlaceholdersInFlatSearchTree,
  FlatSearchTree,
  getValueAtPathFromFlatNode,
  isFlatSearchTreeLeafNode,
} from '@components/LoadSearchTemplateDialog/flatSearchTree';

import { fieldHash } from './types';

type FetchRestartInitialEntitiesParams = {
  token: string;
  domainCode: DomainCode;
  userSub?: string;
  placeholders: ReturnType<typeof findPlaceholdersInFlatSearchTree>;
  initialFlatSearchTemplateTree: FlatSearchTree;
  getAccessTokenSilently: () => Promise<string>;
};

export async function fetchRestartInitialEntities({
  token,
  domainCode,
  userSub,
  placeholders,
  initialFlatSearchTemplateTree,
  getAccessTokenSilently,
}: FetchRestartInitialEntitiesParams): Promise<
  Record<string, { id: number | string; label: string }[]>
> {
  const records: Record<string, { id: number | string; label: string }[]> = {};
  const subFilter = userSub && domainCode ? { sub: userSub, domainCode } : undefined;

  const fetchAndStore = (key: string, entities: { id: number | string; label: string }[]) => {
    if (entities.length > 0) {
      records[key] = entities;
    }
  };

  for (const { nodeId, filterNameAndField } of placeholders) {
    const node = initialFlatSearchTemplateTree.nodes[nodeId];
    if (!node || !isFlatSearchTreeLeafNode(node)) continue;

    const value = getValueAtPathFromFlatNode(node, filterNameAndField);
    if (value === undefined || isTemplatePlaceholder(value)) continue;

    const ids = Array.isArray(value) ? value : [];
    const numIds = ids.filter((x): x is number => typeof x === 'number') as number[];
    const strIds = ids.filter((x): x is string => typeof x === 'string');

    try {
      if (filterNameAndField === 'geometryIds' && numIds.length > 0) {
        const geometries = await getGeometries(token, numIds, subFilter);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          geometries.map((x) => ({
            id: x.id,
            label: `${x.label} [${PRETTY_GEOM_TYPES[x.geometryType]}]`,
          }))
        );
      } else if (
        (filterNameAndField === 'listIds' ||
          filterNameAndField === 'callingStatus.callingStatusListIds') &&
        numIds.length > 0
      ) {
        const lists = await getListsByIds(token, numIds);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          lists.map((x) => ({ id: x.id, label: x.name }))
        );
      } else if (filterNameAndField === 'groupIds' && numIds.length > 0) {
        const groups = await getGroups(token, numIds, domainCode);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          groups.map((x) => ({ id: x.id, label: x.label }))
        );
      } else if (filterNameAndField === 'tagIds' && numIds.length > 0) {
        const tags = await getTagsByIds(token, numIds, domainCode);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          tags.map((x) => ({ id: x.id, label: x.label }))
        );
      } else if (filterNameAndField === 'mailing.mailingIds' && numIds.length > 0) {
        const mailings = await getMailingsByIds(token, numIds);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          mailings.map((x) => ({ id: x.id, label: x.name }))
        );
      } else if (filterNameAndField === 'membership.branchIds' && numIds.length > 0) {
        const branches = await getBranchesById(token, numIds, domainCode);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          branches.map((x) => ({ id: x.id, label: x.label }))
        );
      } else if (filterNameAndField === 'callingStatus.callingStatusUserSubs' && strIds.length > 0) {
        const users = await getUsersByIds(token, strIds);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          users.map((x) => ({ id: x.id, label: x.name }))
        );
      } else if (filterNameAndField === 'callingStatus.callingStatusCampaignIds' && numIds.length > 0) {
        const campaigns = await getCatCampaignsByIds(token, numIds, domainCode);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          campaigns.map((x) => mapCampaignsToTooltips(x))
        );
      } else if (filterNameAndField === 'task.electionIds' && numIds.length > 0) {
        const elections = await getElectionsByIds(token, numIds, domainCode);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          elections.map((x) => ({ id: x.id, label: x.name }))
        );
      } else if (filterNameAndField === 'eventAttendance.eventTypeIds' && numIds.length > 0) {
        const allTypes = await getEventTypes(token);
        const matched = allTypes.filter((t) => numIds.includes(t.id));
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          matched.map((x) => ({ id: x.id, label: x.label }))
        );
      } else if (filterNameAndField === 'eventAttendance.eventIds' && numIds.length > 0) {
        const events = await getEventsByIds(token, numIds, domainCode);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          events.map((x) => ({ id: x.id, label: x.title }))
        );
      } else if (filterNameAndField === 'eventAttendance.eventCampaignIds' && numIds.length > 0) {
        const campaigns = await getCampaignsByIds(token, numIds);
        fetchAndStore(
          fieldHash(nodeId, filterNameAndField),
          campaigns.map((x) => ({ id: x.id, label: x.title }))
        );
      }
    } catch {
      // Ignore fetch errors - form will show empty
    }
  }

  for (const { nodeId, filterNameAndField } of placeholders) {
    if (filterNameAndField !== 'roster.electionId') continue;

    const node = initialFlatSearchTemplateTree.nodes[nodeId];
    if (!node || !isFlatSearchTreeLeafNode(node)) continue;

    const value = getValueAtPathFromFlatNode(node, filterNameAndField);
    if (value === undefined || isTemplatePlaceholder(value)) continue;

    const electionId = typeof value === 'number' ? value : -1;
    if (electionId <= 0) continue;

    try {
      const electionToken = await getAccessTokenSilently();
      const election = await getElectionById(electionToken, electionId);
      records[fieldHash(nodeId, filterNameAndField)] = [{ id: election.id, label: election.name }];
    } catch {
      // Ignore fetch errors
    }
  }

  return records;
}
